import { Tooltip } from 'bootstrap';
import { isEmpty, isSchoolAdmin, getBooleanFilterDefaults } from '../../../library/Core/SmartFunctions';
import { State } from '../../../library/Core/SmartTypes';
import { axiosClient } from '../../../library/Service/axiosClient';

class RecruiterDirectoryService {
    static pageLoader(params: any) {
        const state: any = {};
        const { pageName, selectedCard, searchBy } = params;

        // Fire the initial search in parallel with the other page-config calls (same
        // approach as AlumniDirectoryService.pageLoader) so RecruiterSearchControl can
        // skip its own duplicate initial search — avoids a second loader flash after
        // the route-transition loader finishes.
        const defaultCriteria = RecruiterDirectoryService.getDefaultSearchCriteria(searchBy);
        const cardCriteria = RecruiterDirectoryService.getCriteriaForSelectedCard(
            selectedCard ?? 'totalJoinedAlumni',
            { data: { searchLevel: 'MY_SCHOOL' } } as any
        );
        const initialSearchCriteria = {
            ...defaultCriteria,
            ...(cardCriteria || {}),
            context: { ...defaultCriteria.context, level: 'MY_SCHOOL', page: 1 },
        };

        return Promise.all([
            isSchoolAdmin()
            ? RecruiterDirectoryService.getFilterDomain()
            : axiosClient().get(`${process.env.REACT_APP_REPORTS_SERVER}/recruiter-directory/search-filter-domains`),
            RecruiterDirectoryService.getSummaryCounts('MY_SCHOOL', searchBy),
            axiosClient()
                .post(`${process.env.REACT_APP_REPORTS_SERVER}/recruiter-directory/search`, initialSearchCriteria)
                .catch(() => ({ data: { students: [] } })),
        ]).then((values) => {
            const searchPayload = values[2]?.data;
            state.data = {
                searchLevel: 'MY_SCHOOL',
                searchBy,
                students: searchPayload?.students || [],
                preloadedSearch: true, // tells RecruiterSearchControl to skip the duplicate initial search
                selectedCard: selectedCard ?? 'totalJoinedAlumni',
                summary: values[1].data,
                summaryCountWithoutFilter: values[1].data,
            };

            state.internal = {
                searchFilterDomains: values[0].data
            };
            return Promise.resolve(state);
        });
    }

    static async get(params: any) {
        return await axiosClient().post(`${process.env.REACT_APP_REPORTS_SERVER}/alumni-directory/search`, params);
    }

    static async getFilterDomain() {
        return await axiosClient().get(`${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/recruiter-directory/management-filters`);
    }

    static async performSearchWithUserSelectedCriteria(searchCriteria: any, state: State, dispatch: any, newState: any = {}) {
        const { searchLevel, searchBy } = state.data;
        const defaultCriteria = RecruiterDirectoryService.getDefaultSearchCriteria(searchBy);
        const levelFromSearchCriteria = searchCriteria?.context?.level;

        const criteria = {
            ...defaultCriteria,
            ...searchCriteria,
            context: { ...defaultCriteria.context, ...searchCriteria?.context, level: levelFromSearchCriteria ?? searchLevel },
        };

        const requestURL = `${process.env.REACT_APP_REPORTS_SERVER}/recruiter-directory/search`;
        const summaryResponse = await this.getSummaryCountsBasedOnCampusFilter('MY_SCHOOL', searchCriteria, dispatch);

        if(criteria.context.selectedCard === 'maxPotentialBatch'){
            criteria.passOutYear = criteria.instituteId?.length > 0 ? [ ""+ summaryResponse?.data?.maxPotentialBatch] : searchCriteria?.passOutYear.includes(''+state.data.summaryCountWithoutFilter.maxPotentialBatch) ? searchCriteria?.passOutYear : [ ""+ state.data.summaryCountWithoutFilter.maxPotentialBatch];
        }

        const response = await axiosClient().post(requestURL, criteria);
        dispatch({
            type: 'REFRESH_DATA',
            payload: {
                data: {
                    ...state.data,
                    selectedCard: searchCriteria?.context?.selectedCard ?? state.data.selectedCard,
                    students: response.data.students,
                    filters: response.data.filters,
                    ...newState,
                    summary: summaryResponse?.data || state.data.summaryCountWithoutFilter,
                },
            },
        });
    }

    static async getSummaryCountsBasedOnCampusFilter(level: string = 'MY_SCHOOL', searchFilters: any, dispatch: any) {
        console.log(searchFilters?.instituteId);
        const user = JSON.parse(localStorage.getItem('user') as string);
        const { instituteId: schoolId, instituteGroupId: schoolGroupId, passOutYear } = user;

        // const skillToTarget = RecruiterDirectoryService.getSkillToTarget();
        const schoolCondition = level === 'MY_SCHOOL' ? { schoolId: [schoolId] } : { schoolGroupId: [schoolGroupId], schoolId: [] };
        const searchCriteria = { passOutYear, level, ...schoolCondition, requestedFrom: 'directory' };

        if (!isEmpty(searchFilters?.instituteId)) {
            searchCriteria['schoolId'] = searchFilters?.instituteId;
            const summaryCount = await axiosClient().post(
                `${process.env.REACT_APP_REPORTS_SERVER}/recruiter-directory/recruiter-summary-counts`,
                searchCriteria
            );

            return summaryCount;
        }
    }

    static getCriteriaForSelectedCard = (selectedCard: string, state: State) => {
        const user = JSON.parse(localStorage.getItem('user') as string);
        const { instituteGroupId: schoolGroupId } = user;
        const schoolId = (isSchoolAdmin())  ? user.institutes : [user.instituteId]
        const directoryLevel = state.data.searchLevel;

        const schoolCondition = directoryLevel === 'MY_SCHOOL' ? { schoolId: schoolId } : { schoolGroupId: [schoolGroupId] };

        const criteriaLookup: any = {
            potentialRecruiter: { ...schoolCondition, recruiterStatus: ['POTENTIAL_RECRUITER'] },
            mncs: { ...schoolCondition, isMNC: 1 },
            startups: { ...schoolCondition, isStartUp: 1 },
            recruitedFromCampus: { ...schoolCondition, isRecruitedFromCampus: 1 },
            maxPotentialBatch: { ...schoolCondition, passOutYear: [""+state?.data?.summary?.maxPotentialBatch]},
        };

        return criteriaLookup[selectedCard];
    };

    static async getSummaryCounts(level: string = 'MY_SCHOOL', searchBy: string = '') {
        const user = JSON.parse(localStorage.getItem('user') as string);
        const { instituteId: schoolId, instituteGroupId: schoolGroupId, passOutYear } = user;

        // const skillToTarget = RecruiterDirectoryService.getSkillToTarget();
        const schoolCondition = level === 'MY_SCHOOL' ? { schoolId: [schoolId] } : { schoolGroupId: [schoolGroupId], schoolId: [] };
        const searchCriteria = { passOutYear, level, ...schoolCondition };

        const summaryCount = await axiosClient().post(
            `${process.env.REACT_APP_REPORTS_SERVER}/recruiter-directory/recruiter-summary-counts`,
            searchCriteria
        );

        return summaryCount;
    }

    static getDefaultSearchCriteria(searchBy: string = '') {
        const user = JSON.parse(localStorage.getItem('user') as string);
        const { passOutYear} = user;
        return {
            context: { searchBy, passOutYear, level: 'MY_SCHOOL' },
            recruiterStatus : [],
            passOutYear: [],
            instituteId: [],
            programId: [],
            industryId :[],
            jobCompanyId : [],
            roleId :[],
            ctcOfferedCode : [],
            countryCode :[],
            cityCode:[],
            abilityCode : [],
            willingnessCode : [],
            ...getBooleanFilterDefaults(RecruiterDirectoryService.filterConfig),
        };
    }

    static async getCustomDomains() {
        const user = JSON.parse(localStorage.getItem('user') as string);
        const { instituteId: schoolId, instituteGroupId: schoolGroupId } = user;
    
        return await axiosClient().get(
            `${process.env.REACT_APP_REPORTS_SERVER}/alumni-directory/search-filter-domains?schoolId=${schoolId}&schoolGroupId=${schoolGroupId}`
        );
    }

    static getSearchFilterDomains(domain: any, customDomains: any) {
        const user = JSON.parse(localStorage.getItem('user') as string);
        const { passOutYear } = user;

        const getDomainForCodes = (domain: any, categoryCode: string, codes: any) => {
            if (isEmpty(codes) || !Array.isArray(codes)) return [];
            const stringCodes = codes?.map((code: any) => code?.toString());
            let targetDomain = domain?.get(categoryCode)?.filter((domainValue: any) => stringCodes.includes(domainValue.code));
            if (categoryCode === 'PAST_YEAR_CODE') {
                targetDomain = targetDomain.map((domain: any) =>
                    domain.code == passOutYear ? { ...domain, value: `${domain.value} (your batch)` } : domain
                );
                return targetDomain?.sort((a: any, b: any) => b.value.toLowerCase().localeCompare(a.value.toLowerCase()));
            } else if (categoryCode === 'COUNTRY_CODE') {
                return targetDomain;
            } else if(categoryCode === 'SOCIAL_MEDIA_LEVEL'){
                    return targetDomain.sort(
                    (a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
                );
            }else{
                return targetDomain?.sort((a: any, b: any) => a.value.toLowerCase().localeCompare(b.value.toLowerCase()));
            }
        };
        const searchFilterDomains = {};
        customDomains &&
            // Object.keys(customDomains).forEach((key: any) => {
            //     Object.assign(searchFilterDomains, {
            //         [key]: getDomainForCodes(domain, RecruiterDirectoryService.filterConfig[key]?.domain, customDomains[key]),
            //     });
            // });
            Object.keys(customDomains).forEach((key: any) => {
                    let domainValues = getDomainForCodes(
                        domain,
                        RecruiterDirectoryService.filterConfig[key]?.domain,
                        customDomains[key]
                    );
                    if (key === 'cityCode' && Array.isArray(domainValues)) {
                        const others = domainValues.filter(
                            (d: any) => d.value?.trim().toLowerCase() === 'others'
                        );

                        if (others.length > 1) {
                            const mergedOthers = {
                                ...others[0],
                                value: 'Others',
                                mergedCodes: others.map((d: any) => d.code), 
                            };

                            domainValues = [
                                ...domainValues.filter((d: any) => d.value?.trim().toLowerCase() !== 'others'),
                                mergedOthers,
                            ];
                        }
                    }
                    Object.assign(searchFilterDomains, { [key]: domainValues });
                });
        return searchFilterDomains;
    }

    static getDomainForCodes(domain: any, categoryCode: string, codes: any) {
        return domain.get(categoryCode)?.values.filter((domainValue: any) => codes.includes(domainValue.code));
    }

    static getDomainCategoryCodeForKey(key: string) {
        return this.filterConfig?.[key]?.domain || '';
    }
    
    static getSummaryCardControlImage = (controlName: string) => {
        const controlImageMap: any = {
            totalJoinedAlumni: '',
            pursuingHigherStudies: '',
            workingAlumni: '',
            fromYourBatch: '',
            sameNextLevelInterests: '',
        };
    };

    // static getSkills = () => JSON.parse(localStorage.getItem('user') as string)?.skills?.split(',');
    // static getSkillToTarget = () => JSON.parse(localStorage.getItem('user') as string)?.skillToTarget?.split(',');

    static filterConfig: any = {
        recruiterStatus: {
            id: 'recruiterStatus',
            label: 'Recruiter Status',
            domain: 'RECRUITER_STATUS',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-4',
            isAdditional: false,
            isVisible: true,
            tooltip : 'Current engagement stage of the recruiter in the hiring process.'
        },
        instituteId: {
            id: 'instituteId',
            label: 'Campus',
            domain: 'INSTITUTE_DOMAIN',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-4',
            isAdditional: false,
            isVisible: true,
            tooltip :'Campus linked to the recruiter’s hiring or outreach activity.'
        },
        programId: {
            id: 'programId',
            label: 'Program Name',
            domain: 'ALL_PROGRAM_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-4',
            isAdditional: false,
            isVisible: true,
            tooltip :'Academic program or level targeted by the recruiter.'
        },
        passOutYear: {
            id: 'passOutYear',
            label: 'Batch',
            domain: 'PAST_YEAR_CODE',
            directoryLevel: 'BOTH',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-4',
            isAdditional: false,
            isVisible: true,
            tooltip : ' Academic year group eligible for recruitment.'
        },
        industryId: {
            id: 'industryId',
            label: 'Industry',
            domain: 'INDUSTRY_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-4',
            isAdditional: false,
            isVisible: true,
            tooltip :' Industry sector in which the recruiter operates.'
        },
        jobCompanyId: {
            id: 'jobCompanyId',
            label: 'Company Name',
            domain: 'COMPANY_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem w-25 mb-4',
            isAdditional: false,
            isVisible: true,
            tooltip : 'Name of the organization offering employment opportunities.'
        },
        roleId: {
            id: 'roleId',
            label: 'Roles Hiring For',
            domain: 'FUNCTIONAL_AREA_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem w-25 mb-4',
            isAdditional: false,
            isVisible: true,
            tooltip :'Job roles or functional areas the recruiter is hiring for.'
        },
        abilityCode: {
            id: 'abilityCode',
            label: 'Ability',
            domain: 'SOCIAL_MEDIA_LEVEL',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-4',
            isAdditional: false,
            isVisible: true,
            tooltip :'Recruiter’s hiring capability and readiness level.'
        },
        willingnessCode: {
            id: 'willingnessCode',
            label: 'Willingness',
            domain: 'SOCIAL_MEDIA_LEVEL',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-4',
            isAdditional: false,
            isVisible: true,
            tooltip :'Recruiter’s interest and intent to hire candidates.'
        },
        ctcOfferedCode: {
            id: 'ctcOfferedCode',
            label: 'CTC Offered (In ₹)',
            domain: 'CTC_LPA',
            directoryLevel: 'BOTH',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-4',
            isAdditional: true,
            isVisible: true,
            tooltip :'Salary range offered by recruiters, displayed based on selected language, country, and currency.'
        },
        countryCode: {
            id: 'countryCode',
            label: 'Country',
            domain: 'COUNTRY_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-4',
            isAdditional: true,
            isVisible: true,
            tooltip : 'Job location country associated with the recruiter.'
        },
        cityCode: {
            id: 'cityCode',
            label: 'City',
            domain: 'CITY_CODE',
            directoryLevel: 'BOTH',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-4',
            isAdditional: true,
            isVisible: true,
            tooltip : 'Job location city or region associated with the recruiter.'
        },
        massRecruiter: {
            id: 'massRecruiter',
            label: 'Mass Recruiter',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 1,
            booleanCheckedValue: 1,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        nicheRecruiter: {
            id: 'nicheRecruiter',
            label: 'Niche Recruiter',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 2,
            booleanCheckedValue: 1,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        isFortune500: {
            id: 'isFortune500',
            label: 'Fortune 500',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 3,
            booleanCheckedValue: 1,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        isMNC: {
            id: 'isMNC',
            label: 'MNCs',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 4,
            booleanCheckedValue: 1,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        isListedCompany: {
            id: 'isListedCompany',
            label: 'Listed Companies',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 5,
            booleanCheckedValue: 1,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        isStartUp: {
            id: 'isStartUp',
            label: 'Startup',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 6,
            booleanCheckedValue: 1,
            applicableFor: 'BOTH',
            isVisible: false,
        },
    };
}

export default RecruiterDirectoryService;
